
import { useState, useEffect, useRef } from 'react';
import Footer from '../home/components/Footer';

export default function SkiTrikkePage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const navigateToHome = () => {
    window.REACT_APP_NAVIGATE('/');
    setMenuOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setFormStatus('submitting');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch('https://readdy.ai/api/form/d6irdfutn3rcgj1rb8eg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const reasons = [
    {
      number: '01',
      title: 'A Regional First in Châtel',
      icon: 'ri-award-line',
      body: 'We are the exclusive hub for the 2027 Skki Trikke fleet in the Vonnes sector. When you rent with us, you are accessing the latest in alpine engineering, maintained to the highest professional standards.',
    },
    {
      number: '02',
      title: 'Our Signature "Mise en Main" — Safety First',
      icon: 'ri-shield-check-line',
      body: 'Safety isn\'t an option; it\'s our priority. Every rental includes a mandatory, professional 15-minute orientation. We ensure you are fully confident with the equipment and lift-usage protocols before you ever head to the Gabelou or Portes du Soleil links.',
    },
    {
      number: '03',
      title: 'Inclusivity for All Ages',
      icon: 'ri-group-line',
      body: 'The Skki Trikke is the perfect solution for those who want to stay on the mountain but prefer more stability than traditional skiing. Whether you\'re a senior looking for a low-impact sport, a non-skier wanting to join the family, or simply an adventure seeker, our equipment bridges the gap.',
    },
    {
      number: '04',
      title: 'Prime Location',
      icon: 'ri-map-pin-2-line',
      body: 'Located in the heart of the Vonnes sector, our hub allows for seamless access to the lift network. No unnecessary travel—just high-performance gear ready where you need it.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 md:px-12 lg:px-24 py-4 md:py-6 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={navigateToHome}>
            <img
              src="https://www.dropbox.com/scl/fi/f07ifs1yd05cfcl5cs6mt/234187158.png?rlkey=vl3eoprhczi5ajtexxf2qds9b&st=fmwe88ls&raw=1"
              alt="Vonnes Alpine Life Logo"
              className="h-9 md:h-12 w-auto object-contain"
            />
            <span
              className={`text-base md:text-xl font-light tracking-wide transition-colors ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              VONNES ALPINE LIFE
            </span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-12">
            {['HOME', 'ABOUT', 'CHALET', 'SKI TRIKKE', 'GALLERY', 'CONTACT'].map((item) => {
              const isSkiTrikke = item === 'SKI TRIKKE';
              return (
                <button
                  key={item}
                  onClick={() => {
                    if (isSkiTrikke) {
                      scrollToSection('hero');
                    } else {
                      navigateToHome();
                      setTimeout(() => {
                        const element = document.getElementById(item.toLowerCase().replace(' ', '-'));
                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }
                  }}
                  className={`text-sm font-light tracking-widest transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    scrolled
                      ? 'text-gray-600 opacity-80 hover:text-gray-900 hover:opacity-100'
                      : 'text-white opacity-60 hover:text-white hover:opacity-100'
                  }`}
                >
                  {item}
                </button>
              );
            })}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-2xl ${scrolled ? 'text-gray-900' : 'text-white'} ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white shadow-xl border-t border-gray-100">
            {['HOME', 'ABOUT', 'CHALET', 'SKI TRIKKE', 'GALLERY', 'CONTACT'].map((item) => (
              <button
                key={item}
                onClick={() => {
                  if (item === 'SKI TRIKKE') {
                    scrollToSection('hero');
                  } else {
                    navigateToHome();
                  }
                }}
                className="w-full text-left px-6 py-4 text-sm font-light tracking-widest text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-50"
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://skki-trikke.eu/wp-content/uploads/2025/09/facewow_2025-09-14_12-09-30-1536x960.jpg"
            alt="Skki Trikke in action on the Portes du Soleil slopes"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
        </div>

        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 md:px-12 lg:px-24 w-full">
          <div style={{ marginTop: '-5%' }}>
            <p
              className="text-sm md:text-lg font-light tracking-[4px] md:tracking-[6px] uppercase mb-4 md:mb-6"
              style={{ color: '#D4AF37' }}
            >
              Skki Trikke Rentals — The Future of the Slopes
            </p>
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-thin tracking-wider text-white mb-4 md:mb-6 leading-tight"
              style={{ letterSpacing: '2px', fontFamily: 'Cormorant Garamond, serif' }}
            >
              Discover the Portes du Soleil
              <br />
              with Confidence.
            </h1>
            <p
              className="text-lg md:text-xl lg:text-2xl font-light text-white/80 mb-10 md:mb-14 max-w-3xl mx-auto"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              The most inclusive, stable, and thrilling way to carve the mountains.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => scrollToSection('why-us')}
                className="group flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <span className="text-base md:text-lg font-light tracking-wide">Why Rent With Us</span>
                <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-black text-white group-hover:bg-gray-800 transition-colors">
                  <i className="ri-arrow-down-line text-lg"></i>
                </div>
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="flex items-center gap-3 border border-white/60 text-white px-8 py-4 rounded-full hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <span className="text-base md:text-lg font-light tracking-wide">Book Now</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Rent With Us */}
      <section id="why-us" className="py-16 md:py-24 lg:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-sm tracking-[4px] uppercase mb-4" style={{ color: '#D4AF37' }}>
              Our Promise
            </p>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-thin tracking-wide text-gray-900"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Why Rent with Vonnes Alpine Life?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {reasons.map((r) => (
              <div
                key={r.number}
                className="group bg-gray-50 rounded-lg p-6 md:p-10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start gap-4 md:gap-6">
                  <div
                    className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ backgroundColor: '#D4AF37' }}
                  >
                    <i className={`${r.icon} text-xl md:text-2xl text-white`}></i>
                  </div>
                  <div>
                    <p className="text-xs tracking-[3px] uppercase text-gray-400 mb-2">{r.number}</p>
                    <h3
                      className="text-xl md:text-2xl font-light text-gray-900 mb-3 md:mb-4"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {r.title}
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">{r.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Details */}
      <section id="equipment" className="py-16 md:py-24 lg:py-32 px-6 md:px-12 lg:px-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-sm tracking-[4px] uppercase mb-4" style={{ color: '#D4AF37' }}>
              The Hardware
            </p>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-thin tracking-wide text-gray-900"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              The Equipment: 2027 Skki Trikke Fleet
            </h2>
          </div>

          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative h-80 sm:h-96 md:h-auto md:min-h-[520px]">
                <img
                  src="https://skki-trikke.eu/wp-content/uploads/2021/08/FB_IMG_1674074438815-300x300.jpg"
                  alt="2027 Skki Trikke Fleet"
                  className="w-full h-full object-contain bg-gray-100"
                />
              </div>

              <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
                <h3
                  className="text-3xl md:text-4xl font-light text-gray-900 mb-3"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Skki Trikke 2027
                </h3>
                <p className="text-base md:text-lg text-gray-500 mb-8 md:mb-10 tracking-wide">Latest Generation Fleet</p>

                <div className="space-y-5 md:space-y-6 mb-10 md:mb-12">
                  {[
                    { icon: 'ri-settings-3-line', title: 'Technical Excellence', desc: 'Fully compliant with STRMTG French lift regulations — cleared for use across the entire Portes du Soleil network.' },
                    { icon: 'ri-hand-heart-line', title: 'Ease of Use', desc: 'Intuitive "handlebar" carving that mimics the natural movement of the body — no prior ski experience required.' },
                    { icon: 'ri-shield-star-line', title: 'Safety', desc: 'Three points of contact with the snow for maximum stability and control — the most secure way to enjoy the mountain.' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4 md:gap-5">
                      <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl md:text-2xl`} style={{ color: '#D4AF37' }}></i>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium mb-1">{item.title}</p>
                        <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => scrollToSection('contact')}
                  className="bg-black text-white px-8 md:px-10 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 text-base md:text-lg font-light tracking-wide cursor-pointer whitespace-nowrap w-fit"
                >
                  Enquire About Rentals
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 lg:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-sm tracking-[4px] uppercase mb-4" style={{ color: '#D4AF37' }}>
              Simple Process
            </p>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-thin tracking-wide text-gray-900"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            {[
              { icon: 'ri-calendar-check-line', step: '1', title: 'Book', desc: 'Contact us to reserve your Skki Trikke. We\'ll confirm availability and arrange your rental dates.' },
              { icon: 'ri-user-settings-line', step: '2', title: 'Mise en Main', desc: 'Our team provides a mandatory 15-minute professional orientation covering equipment use and lift protocols.' },
              { icon: 'ri-mountain-line', step: '3', title: 'Ride', desc: 'Experience the Portes du Soleil in a revolutionary new way — with enhanced stability, confidence, and control.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div
                  className="w-20 h-20 md:w-28 md:h-28 mx-auto mb-6 md:mb-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#D4AF37' }}
                >
                  <i className={`${s.icon} text-4xl md:text-5xl text-white`}></i>
                </div>
                <h3
                  className="text-2xl md:text-3xl font-light text-gray-900 mb-3 md:mb-4"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {s.step}. {s.title}
                </h3>
                <p className="text-gray-600 text-base md:text-lg leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rental Info Banner */}
      <section className="py-14 md:py-20 px-6 md:px-12 lg:px-24" style={{ backgroundColor: '#D4AF37' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 text-center text-white">
            {[
              { icon: 'ri-shield-check-line', title: 'Safety First', desc: 'Mandatory 15-minute professional orientation included with every rental' },
              { icon: 'ri-award-line', title: "Châtel's First Hub", desc: 'Exclusive 2027 fleet in the Vonnes sector — the only dedicated Skki Trikke hub in the area' },
              { icon: 'ri-map-pin-2-line', title: 'Prime Access', desc: 'Heart of the Vonnes sector — seamless access to the full lift network' },
            ].map((item) => (
              <div key={item.title}>
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className={`${item.icon} text-4xl md:text-5xl`}></i>
                </div>
                <h3 className="text-xl md:text-2xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {item.title}
                </h3>
                <p className="text-base md:text-lg opacity-90">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Booking Form */}
      <section id="contact" className="py-16 md:py-24 lg:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm tracking-[4px] uppercase mb-4" style={{ color: '#D4AF37' }}>
              Reserve Your Gear
            </p>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-thin tracking-wide text-gray-900 mb-4 md:mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Book Your Skki Trikke
            </h2>
            <p className="text-lg md:text-xl text-gray-600">
              Ready to experience the slopes in a revolutionary new way? Get in touch to reserve your equipment.
            </p>
          </div>

          <div className="bg-gray-50 p-6 md:p-10 lg:p-12 rounded-lg shadow-xl">
            {formStatus === 'success' ? (
              <div className="text-center py-12 md:py-16">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-6 rounded-full" style={{ backgroundColor: '#D4AF37' }}>
                  <i className="ri-check-line text-3xl md:text-4xl text-white"></i>
                </div>
                <h3 className="text-2xl md:text-3xl font-light text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Enquiry Sent!
                </h3>
                <p className="text-gray-600 text-base md:text-lg mb-8">Thank you — we'll be in touch shortly to confirm your booking.</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="bg-black text-white px-10 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 text-lg font-light tracking-wide cursor-pointer whitespace-nowrap"
                >
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5 md:space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">FIRST NAME</label>
                    <input type="text" name="first_name" required className="w-full px-5 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">LAST NAME</label>
                    <input type="text" name="last_name" required className="w-full px-5 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">EMAIL</label>
                  <input type="email" name="email" required className="w-full px-5 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm" />
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">PHONE</label>
                  <input type="tel" name="phone" required className="w-full px-5 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">RENTAL START DATE</label>
                    <input type="date" name="start_date" required className="w-full px-5 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">RENTAL END DATE</label>
                    <input type="date" name="end_date" required className="w-full px-5 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">NUMBER OF SKKI TRIKKES</label>
                  <select name="quantity" required className="w-full px-5 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm cursor-pointer">
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-light text-gray-700 mb-2 tracking-wide">MESSAGE</label>
                  <textarea
                    name="message"
                    rows={5}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-5 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm resize-none"
                    placeholder="Tell us about your experience level and any special requirements..."
                  ></textarea>
                  <p className={`text-xs mt-2 ${charCount > 500 ? 'text-red-500' : 'text-gray-500'}`}>
                    {charCount}/500 characters
                  </p>
                </div>

                {formStatus === 'error' && (
                  <p className="text-red-500 text-sm text-center">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting' || charCount > 500}
                  className="w-full bg-black text-white py-5 rounded-full hover:bg-gray-800 transition-all duration-300 text-lg font-light tracking-wide cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Enquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
